import type { TaskSource, TaskStatus } from "@/api/tasks";
import { translate } from "@/i18n";
import type en from "@/i18n/messages/panels-tasks/en";
import { panelsTasksMessages } from "@/i18n/messages/panels-tasks";
import type { TaskValueMenuOption } from "./task-value-menu";

type TaskMessageKey = keyof typeof en;

/** Task priorities in menu order; names come from `TASK_PRIORITY_LABEL_KEYS`. */
export const PRIORITIES = ["low", "medium", "high", "urgent"] as const;

type Transition = {
  target: TaskStatus;
  labelKey: TaskMessageKey;
  destructive?: boolean;
};
/**
 * Status buttons shown in the task detail panel: the usual next steps from
 * each status. A person may still drag the task to any column on the board.
 */
const CANCEL: Transition = {
  target: "backlog",
  labelKey: "cancelTask",
  destructive: true,
};

export const STATUS_TRANSITIONS: Record<TaskStatus, Transition[]> = {
  backlog: [
    { target: "todo", labelKey: "moveToTodo" },
    { target: "in_progress", labelKey: "start" },
    { target: "blocked", labelKey: "block" },
  ],
  todo: [
    { target: "in_progress", labelKey: "start" },
    { target: "backlog", labelKey: "moveToBacklog" },
    { target: "blocked", labelKey: "block" },
  ],
  in_progress: [
    { target: "in_review", labelKey: "review" },
    { target: "done", labelKey: "markDone" },
    { target: "todo", labelKey: "moveToTodo" },
    { target: "blocked", labelKey: "block" },
    CANCEL,
  ],
  in_review: [
    { target: "done", labelKey: "approve" },
    { target: "in_progress", labelKey: "rework" },
    { target: "todo", labelKey: "moveToTodo" },
    CANCEL,
  ],
  done: [
    { target: "todo", labelKey: "reopen" },
    { target: "in_progress", labelKey: "restart" },
    { target: "backlog", labelKey: "moveToBacklog" },
  ],
  blocked: [
    { target: "todo", labelKey: "unblock" },
    { target: "in_progress", labelKey: "start" },
    CANCEL,
  ],
};

/** The status menu's items (the pocket's Status row, the wall's new task card); Cancel is destructive. */
export function taskStatusOptions(
  status: TaskStatus,
): TaskValueMenuOption<TaskStatus>[] {
  return (STATUS_TRANSITIONS[status] ?? []).map((transition) => ({
    value: transition.target,
    label: translate(panelsTasksMessages, transition.labelKey),
    destructive: transition.destructive,
  }));
}

export const SOURCE_LABEL_KEYS = {
  note: "sourceNote",
  chat: "sourceChat",
  heartbeat: "sourceHeartbeat",
  schedule: "sourceSchedule",
  comment_mention: "sourceMention",
  task_assigned: "sourceAssignment",
  delegation: "sourceDelegation",
  event: "sourceEvent",
  template: "sourceTemplate",
} as const satisfies Record<TaskSource, TaskMessageKey>;

/** A source's label; one this build does not know (a newer server's) has none. */
export function sourceLabelKey(source: string): TaskMessageKey | null {
  return Object.prototype.hasOwnProperty.call(SOURCE_LABEL_KEYS, source)
    ? SOURCE_LABEL_KEYS[source as TaskSource]
    : null;
}
